import { Box, Typography } from "@mui/material";
import { useUpdateMerchantNoteMutation } from "Api/Mutations/Merchants/useUpdateMerchantNotesMutation";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const RESOURCES = Resources.Hunter.Merchants.CompleteRegistration.Notes;
const MAX_NOTE_LENGTH = 2000;

type FormModel = { note: string };

type Props = {
  merchantPublicID: string;
  onAfterSubmit: () => void;
  formId: string;
  defaultValues: FormModel;
};

export const MerchantNotesForm = ({
  merchantPublicID,
  onAfterSubmit,
  formId,
  defaultValues,
}: Props) => {
  const { t } = useResource();

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    watch,
  } = useForm<FormModel>({
    defaultValues,
  });

  const { mutate: updateMerchantNote, isPending } =
    useUpdateMerchantNoteMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveSuccess));
        onAfterSubmit();
      },
      onError: () => toast.error(t(RESOURCES.SaveError)),
    });

  const onSubmit = ({ note }: FormModel) => {
    if (isPending) {
      return;
    }

    if (!isDirty) {
      return onAfterSubmit();
    }

    updateMerchantNote({
      publicID: merchantPublicID,
      note,
    });
  };

  const note = watch("note");

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormSection legend={t(RESOURCES.Title)} noMargins>
        <FormInput
          control={control}
          name="note"
          disablePadding
          aria-label={t(RESOURCES.Label)}
          type="textarea"
          rows={10}
          maxLength={MAX_NOTE_LENGTH}
          helperText={
            <Box display="flex" justifyContent="flex-end" component="span">
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {`${note.length} / ${MAX_NOTE_LENGTH}`}
              </Typography>
            </Box>
          }
        />
      </FormSection>
    </form>
  );
};
